import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Tabs } from '../components/ui/Overlay';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { AsyncBoundary, Skeleton } from '../components/ui/AsyncBoundary';
import { VehicleStateBadge, HealthBadge, LastSeen } from '../components/domain/VehicleBits';
import { EditVehicleModal } from '../components/domain/VehicleModals';
import { useResource } from '../hooks/useResource';
import { useAuth } from '../auth/AuthProvider';

import { LiveTab } from './vehicle/LiveTab';
import { HistoryTab } from './vehicle/HistoryTab';
import { TripsTab } from './vehicle/TripsTab';
import { EventsTab } from './vehicle/EventsTab';

/**
 * One vehicle, four views.
 *
 * The header is shared across all four so the thing you are looking at never
 * leaves the screen — switching from Live to History should not feel like
 * navigating away from the vehicle.
 */
export function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tab, setTab] = useState('live');
  const [editing, setEditing] = useState(false);

  const vehicle = useResource(`/api/vehicles/${id}`, null, { pollMs: 60_000 });
  const fleets = useResource(can('vehicle:write') ? '/api/fleets' : null);

  const tabs = [
    { value: 'live', label: 'Live', icon: 'location' },
    { value: 'history', label: 'History', icon: 'history' },
    { value: 'trips', label: 'Trips', icon: 'route' },
    { value: 'events', label: 'Events', icon: 'bell' },
  ];

  return (
    <Page className="space-y-4">
      <AsyncBoundary state={vehicle} skeleton={<Skeleton className="h-16 w-full" />}>
        {(data) => {
          const v = data.vehicle;
          return (
            <PageHeader
              breadcrumb={
                <Link
                  to="/vehicles"
                  className="mb-1 inline-flex items-center gap-1 text-[12.5px] text-ink-3 hover:text-ink-2"
                >
                  <Icon name="chevronLeft" size="xs" />
                  Vehicles
                </Link>
              }
              title={v.reg_number}
              subtitle={
                [v.make, v.model, v.year].filter(Boolean).join(' ') +
                (v.fleet_name ? ` · ${v.fleet_name}` : '')
              }
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  <VehicleStateBadge vehicle={v} />
                  <HealthBadge score={v.health_score} online={v.online} flags={v.health_flags} />
                  <LastSeen at={v.last_packet_at} online={v.online} />
                  {can('vehicle:write') && (
                    <Button variant="ghost" size="sm" icon="filter" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                  {v.device_id && can('device:read') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="chip"
                      onClick={() => navigate(`/devices/${v.device_id}`)}
                    >
                      Device
                    </Button>
                  )}
                </div>
              }
            />
          );
        }}
      </AsyncBoundary>

      <EditVehicleModal
        open={editing}
        vehicle={vehicle.data?.vehicle}
        fleets={fleets.data?.fleets || []}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          vehicle.refetch();
        }}
      />

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === 'live' && <LiveTab vehicleId={id} vehicle={vehicle.data?.vehicle} />}
      {tab === 'history' && <HistoryTab vehicleId={id} />}
      {tab === 'trips' && <TripsTab vehicleId={id} />}
      {tab === 'events' && <EventsTab vehicleId={id} />}
    </Page>
  );
}
